/**
 * Color Siblings Selector (Hawkers-style)
 *
 * Finds every product that shares the current product's "modelo" (read from
 * the custom.modelo_ metafield, passed in via data-modelo) and renders a
 * row of thumbnails linking to each one.
 *
 * Why this runs in JS instead of pure Liquid: Shopify's {% paginate %} tag
 * always reads which page to show from the current request's URL, and a
 * product page never has a `?page=2` in its address - so a Liquid loop over
 * collections.all.products can only ever see the first 250 products, no
 * matter how it's written. A browser fetch() has no such limit: it can ask
 * for page 2, 3, etc. on its own, so this scans the whole catalog instead
 * of just the first slice of it.
 *
 * The public /products.json endpoint doesn't expose metafields, only tags,
 * so matching here is tag-based: every product with a modelo_ value is
 * expected to also carry that value as a tag (added once, store-wide, when
 * this feature was set up). Comparison is case-insensitive so that minor
 * casing differences between siblings (e.g. "Enigma" vs "enigma") don't
 * break the match.
 */
(function () {
  var PRODUCTS_PER_PAGE = 250;
  var MAX_PAGES = 10; // safety cap: 2500 products, well above the current catalog size.

  function normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  function handleFromUrl(url) {
    var match = (url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  class ColorSiblingsElement extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    async init() {
      var modelo = this.dataset.modelo;
      if (!modelo) return;

      var targetModelo = normalize(modelo);
      var currentHandle = handleFromUrl(this.dataset.currentUrl);

      var products;
      try {
        products = await this.fetchAllProducts();
      } catch (error) {
        // Fail silently on the storefront: the product page itself still works
        // fine without the selector, this is a progressive enhancement.
        return;
      }

      var siblings = products.filter(function (product) {
        var tags = product.tags || [];
        return tags.some(function (tag) {
          return normalize(tag) === targetModelo;
        });
      });

      // Deduplicate by handle in case a product appeared on more than one page.
      var seen = {};
      siblings = siblings.filter(function (product) {
        if (seen[product.handle]) return false;
        seen[product.handle] = true;
        return true;
      });

      if (siblings.length < 2) return;

      this.render(siblings, currentHandle);
    }

    async fetchAllProducts() {
      var all = [];
      for (var page = 1; page <= MAX_PAGES; page++) {
        var response = await fetch(
          '/collections/all/products.json?limit=' + PRODUCTS_PER_PAGE + '&page=' + page
        );
        if (!response.ok) break;
        var data = await response.json();
        var pageProducts = data.products || [];
        if (pageProducts.length === 0) break;
        all = all.concat(pageProducts);
        if (pageProducts.length < PRODUCTS_PER_PAGE) break;
      }
      return all;
    }

    render(siblings, currentHandle) {
      var label = document.createElement('p');
      label.className = 'color-siblings__label';
      label.textContent = siblings.length + ' colores';

      var grid = document.createElement('div');
      grid.className = 'color-siblings__grid';

      siblings.forEach(function (product) {
        var link = document.createElement('a');
        link.href = '/products/' + product.handle;
        link.className = 'color-siblings__item';
        link.title = product.title || '';

        if (product.handle === currentHandle) {
          link.classList.add('is-active');
          link.setAttribute('aria-current', 'true');
        }

        var image = product.images && product.images[0];
        if (image && image.src) {
          var img = document.createElement('img');
          var separator = image.src.indexOf('?') === -1 ? '?' : '&';
          img.src = image.src + separator + 'width=140';
          img.alt = product.title || '';
          img.width = 64;
          img.height = 64;
          img.loading = 'lazy';
          link.appendChild(img);
        }

        grid.appendChild(link);
      });

      this.appendChild(label);
      this.appendChild(grid);
      this.removeAttribute('hidden');
    }
  }

  if (!customElements.get('color-siblings')) {
    customElements.define('color-siblings', ColorSiblingsElement);
  }
})();
